import { createReducer, on } from '@ngrx/store';
import { IAuthUser } from '@core/interfaces/auth-user.interface';
import { AuthActions } from './auth.actions';

export interface IAuthState {
  init: boolean;
  authenticated: boolean;
  isSecurityAdministrator: boolean;
  subject: string | null;
  accessStatus: 'idle' | 'loading' | 'ready' | 'failed';
  currentUser: IAuthUser | null;
  favoriteUrl: string | null;
  access: string[];
  error: string | null;
  sessionTransitioning: boolean;
  synchronizationBaseline: {
    readonly subject: string | null;
    readonly access: readonly string[];
    readonly initial: boolean;
  } | null;
}

export const initialState: IAuthState = {
  init: false,
  authenticated: false,
  isSecurityAdministrator: false,
  subject: null,
  accessStatus: 'idle',
  currentUser: null,
  favoriteUrl: null,
  access: [],
  error: null,
  sessionTransitioning: false,
  synchronizationBaseline: null,
};

export const authReducer = createReducer(
  initialState,
  on(AuthActions.initEnd, (state): IAuthState => ({ ...state, init: true })),
  on(
    AuthActions.sessionChanged,
    (state, { session }): IAuthState => ({
      ...state,
      authenticated: session.authenticated,
      isSecurityAdministrator: session.isSecurityAdministrator,
      subject: session.subject,
      currentUser: null,
      access: [],
      error: null,
      accessStatus: session.authenticated ? 'loading' : 'idle',
      sessionTransitioning: session.authenticated,
      synchronizationBaseline: session.authenticated
        ? (state.synchronizationBaseline ?? {
            subject: state.subject,
            access: state.access,
            initial: !state.init || !state.authenticated,
          })
        : null,
    }),
  ),
  on(
    AuthActions.sessionSynchronizationCompleted,
    (state, action): IAuthState =>
      state.subject === action.subject
        ? {
            ...state,
            currentUser: action.user,
            access: action.accessLoadFailed ? [] : action.access,
            accessStatus: action.accessLoadFailed ? 'failed' : 'loading',
          }
        : state,
  ),
  on(
    AuthActions.sessionSynchronizationReleased,
    (state): IAuthState => ({
      ...state,
      accessStatus: state.accessStatus === 'failed' ? 'failed' : 'ready',
      sessionTransitioning: false,
      synchronizationBaseline: null,
    }),
  ),
  on(
    AuthActions.sessionFailed,
    (state, { message }): IAuthState => ({
      ...state,
      error: message,
      accessStatus: 'failed',
      access: [],
      currentUser: null,
      sessionTransitioning: false,
      synchronizationBaseline: null,
    }),
  ),
  on(
    AuthActions.requestAccess,
    (state): IAuthState => ({
      ...state,
      accessStatus: 'loading',
      error: null,
    }),
  ),
  on(
    AuthActions.logout,
    (state): IAuthState => ({
      ...state,
      accessStatus: 'idle',
      sessionTransitioning: true,
    }),
  ),
  on(AuthActions.setFavoriteUrl, (state, { favoriteUrl }) => ({
    ...state,
    favoriteUrl,
  })),
);
